import { CategoryDownloadDto, CompetenceDownloadDto } from 'collaboration-service';
import LabelInfo from 'components/LabelInfo';
import { MediaType } from 'components/MediaSelection/FileSelector';
import MissingInput from 'components/MissingInput';
import { belowBreakpointOrEqual, Button, Divider, Input, Label, LinkTo, Loader, styled } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { Actions } from 'services/ApplicationState/Actions';
import { useAppDispatch } from 'services/ApplicationState/ApplicationState';
import { shallowCompare } from 'services/Helpers';
import { gotoNamedRoute } from 'services/Helpers/RoutingHelper';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import History from 'services/History';
import { ImgI18NTranslateFunction, useImgI18N } from 'services/ImgI18N';
import { ThemeContext } from 'styled-components';
import { CMSSC as W } from 'views/CMS/CMSSC';
import EditLanguageStrings from '../Editors/EditLanguageStrings';
import EditMedia from '../Editors/EditMedia';
import { columnActionWidth, columnFlagWidth, EditorsSC as T } from '../Editors/EditorsSC';
import EditPartContainer, { useEditPartContainer } from '../EditPartContainer';
import EditPartLabelAndEditor, { EPLContainer } from '../EditPartLabelAndEditor';
import { GeneralPostEditorProps, useDispatchGPE, useSelectorGPE } from '../GeneralPostEdit';
import { GeneralPostEditActions, GeneralPostEditState } from '../GeneralPostEditStateHandling';
import { savePost } from './PostEdit';
import EditNumber from '../Editors/EditNumber';
import EditString from '../Editors/EditString';

export const MissingTexts = {
    media: "media is missing.",
    headlines: "name is missing.",
    descriptions: "description is missing.",
    categories: "category is missing.",
    location: "location is missing.",
    contentType: "content type is missing.",
    mediaType: "media type is missing.",
    content: "content is missing.",
    contentContent: "content for {{lng}} is missing.",
    contentLink: "link for {{lng}} is missing.",
    uploadRunning: "there are not finish uploads.",
    filesInUploadQueue: "there are files in your upload queue.",
    videosMissing: "videos are missing.",
    set3Categories: "at least 3 categories are needed.",
};

const defCalcMissing: (post: CategoryDownloadDto, missing: string[], t: ImgI18NTranslateFunction) => void =
    (post, missing, t) => {
        if ((post.names ?? []).length === 0)
            missing.push(t(MissingTexts.headlines));
    };

const gridWith = columnActionWidth + columnFlagWidth;
export const G = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: ${p => gridWith}px 1fr 1fr;
`;
const LabelC = styled.div`
    padding-top: 7px;
    font-size:0.9rem;
    width: 100%;
    padding-bottom: ${props => belowBreakpointOrEqual(props, "tablet") ? "5px" : "unset"}; 
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
`;

const mapper = (s: GeneralPostEditState) => ({ post: s.post, files: s.files, originalPost: s.originalPost });

const CategoryEdit = (p: GeneralPostEditorProps<CategoryDownloadDto>) => {
    const { onSave, onCancel, onChangeNeedSave, onMount, onDismount, apiRoute, path } = p;
    const { post, files, originalPost } = useSelectorGPE(mapper, shallowCompare);
    const dispatchGPE = useDispatchGPE();
    const dispatch = useAppDispatch();
    const [uploading, setUploading] = React.useState<boolean>(false);
    const { t } = useImgI18N("admin");
    const uEPC = useEditPartContainer();
    const [scrollHelper] = React.useState(new ScrollHelper());
    const theme = React.useContext(ThemeContext);
    const isSmallDevice = belowBreakpointOrEqual({ theme }, "tablet");
    const pp = post as CategoryDownloadDto;

    const scrollTo = React.useMemo(() => (id: string) => {
        scrollHelper.scrollTo(id);
    }, [scrollHelper]);

    React.useEffect(() => {
        dispatch(Actions.setScrollTo(scrollTo));
        return () => { dispatch(Actions.setScrollTo(undefined)); }
    }, [scrollTo, dispatch]);

    const saveInt = React.useMemo(() => () => {
        setUploading(true);
        const save = async () => {
            if (onSave) {
                const newPost = await savePost(pp, files, apiRoute ?? "api/content/");
                if (newPost)
                    onSave(newPost);
                dispatchGPE(GeneralPostEditActions.batch([
                    GeneralPostEditActions.setOriginalPost(newPost),
                    GeneralPostEditActions.setPost(newPost),
                ]));
                setUploading(false);
                if (onChangeNeedSave)
                    onChangeNeedSave(false);
            }
            History.goBack();
        }
        save();
    }, [onSave, pp, files, dispatchGPE, onChangeNeedSave, apiRoute]);

    React.useEffect(() => {
        dispatch(Actions.setSave(saveInt));
        return () => { dispatch(Actions.setSave(undefined)); }
    }, [dispatch, saveInt]);

    const cancelInt = React.useMemo(() => () => {
        dispatchGPE(GeneralPostEditActions.setPost(originalPost));
        if (onChangeNeedSave)
            onChangeNeedSave(false);
        if (onCancel)
            onCancel();
    }, [onCancel, dispatchGPE, originalPost, onChangeNeedSave]);

    React.useEffect(() => {
        if (onMount)
            onMount(saveInt, cancelInt);
        return () => {
            if (onDismount)
                onDismount();
        }
    }, [onMount, onDismount, saveInt, cancelInt])

    const missing = React.useMemo(() => {
        const toRet: string[] = [];
        const check = defCalcMissing;
        if (pp)
            check(pp, toRet, t);
        return toRet;
    }, [pp, t]);

    const needSave = !(missing.length > 0 || _.isEqual(originalPost, post));
    React.useEffect(() => {
        if (onChangeNeedSave)
            onChangeNeedSave(needSave);
    }, [needSave, onChangeNeedSave]);

    const changeCategoryType = (value?: string) => {
        if (pp) {
            const newPost = _.clone(pp);
            newPost.categoryType = value ?? '';
            dispatchGPE(GeneralPostEditActions.setPost(newPost));
        }
    }
    const regexp = /^[0-9\b]+$/;
    const onChangeOrder = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (pp) {
            const value = event.target.value;
            if (value === '' || regexp.test(value)) {
                const newPost = _.clone(pp);
                newPost.order = parseInt(value);
                dispatchGPE(GeneralPostEditActions.setPost(newPost));
            }
        }
    }

    const renderMoreContent = React.useMemo(() => {
        switch (pp.type!) {
            case "Competence":
                return (
                    <EditPartContainer icon="more" title='more settings' translatedTitle={t("more settings")} {...uEPC} >
                        <EditPartLabelAndEditor>
                            {[
                                { label: { icon: "stats", content: "default value" }, editor: <EditNumber<CompetenceDownloadDto> getter={x => x.defaultValue} min={0} max={10} step={0.1} /> },
                                { label: { icon: "stats", content: "decrease after days" }, editor: <EditNumber<CompetenceDownloadDto> getter={x => x.decreaseAfterDays} min={0} max={10} step={1} /> },
                                { label: { icon: "stats", content: "decrease by" }, editor: <EditString<CompetenceDownloadDto> getter={x => x.decreaseOverTimeBy} /> },
                                { label: { icon: "stats", content: "apply skill level formula" }, editor: <EditString<CompetenceDownloadDto> getter={x => x.formula} /> },
                            ]}
                        </EditPartLabelAndEditor>
                    </EditPartContainer>
                );
            default:
                return null;
        }
    }, [uEPC, pp.type, t]);

    if (!post)
        return <Loader animation="loader_dna" active />

    const linkText = (t('{{task}} {{type}}', { task: pp.id === '' ? "add" : "edit", type: pp.type ?? '' }));
    // const linkText = (pp.id === '' ? t('new') : t('edit')) + ' ' + (pp.type ?? '');

    console.log("CategoryEdit.tsx::169 => path", path);

    return (
        <>
            <div ref={scrollHelper.getRef("top")} />
            {path && path.length === 2 &&
                <W.CategoryHeaderContainer>
                    <LinkTo
                        noAnimation
                        icon="home"
                        noBoldOnHover
                        hoverColor="@accent"
                        onClick={() => gotoNamedRoute(path[0])}
                    />
                    <LinkTo
                        noAnimation
                        icon="chevron right"
                        noBoldOnHover
                        hoverColor="@accent"
                        linkText={path[0]}
                        onClick={() => gotoNamedRoute(path[0])}
                    />
                    <LinkTo
                        noBoldOnHover
                        icon="chevron right"
                        noAnimation
                        linkText={t("manage {{path}}", { path: t(path[1]) })}
                        onClick={() => gotoNamedRoute(path[1])}
                    />
                    <W.BreadcrumbActiveSite>
                        <LinkTo
                            noBoldOnHover
                            icon="chevron right"
                            noAnimation
                            linkText={linkText}
                            noLink
                            boldText
                        />
                    </W.BreadcrumbActiveSite>
                </W.CategoryHeaderContainer>
            }
            <div style={{ marginTop: -30 }} />
            <div>
                <EditPartContainer icon="edit" translatedTitle={linkText} title='' bold >

                    <T.Container style={{ paddingTop: 0 }}>
                        <Loader
                            active={uploading}
                            animation="loader_dna"
                        />
                        {missing.length > 0 &&
                            <>
                                <EditPartContainer icon="language" title='' translatedTitle={t("missing inputs")} {...uEPC}>
                                    <MissingInput title={t("missing inputs")} missing={missing} />
                                </EditPartContainer>
                                <div style={{ marginTop: -20 }} />
                            </>
                        }
                        <div ref={scrollHelper.getRef("names")} />
                        <EditPartContainer icon="language" title='' translatedTitle={t("names")} {...uEPC} >
                            <EditLanguageStrings<CategoryDownloadDto> getter={x => x.names} editorFormats={[]} noHtml hideToolbar saveText={t("confirm name")} saveIcon='check' />
                        </EditPartContainer>

                        <div ref={scrollHelper.getRef("settings")} />
                        <EditPartContainer icon="cog" title='' translatedTitle={t("settings")} {...uEPC}>
                            <div style={{ display: "grid", gridTemplateColumns: isSmallDevice ? "1fr" : "1fr 1fr", columnGap: 80 }}>
                                <EPLContainer >
                                    <LabelC>
                                        <Label content={t("{{type}} type", { type: pp.type ?? '' })} icon="tag" />
                                        <LabelInfo infoText={t("category/location edit - type description")} />
                                    </LabelC>
                                    <Input value={pp.categoryType} change={changeCategoryType} />
                                </EPLContainer>
                                <EPLContainer minimized>
                                    <LabelC>
                                        <Label content="order" icon="chevron up down" />
                                        <LabelInfo infoText={t("category/location edit - order description")} />
                                    </LabelC>
                                    <Input value={pp.order ?? ''} onChange={onChangeOrder} type="number" />
                                </EPLContainer>
                            </div>
                        </EditPartContainer>

                        <div ref={scrollHelper.getRef("media")} />
                        <EditPartContainer icon="image effects" title='' translatedTitle={t("media")} {...uEPC}>
                            <div style={{ display: "grid", gridTemplateColumns: isSmallDevice ? "1fr" : "1fr 1fr", columnGap: isSmallDevice ? 0 : 80 }}>
                                <EditMedia<CategoryDownloadDto> getter={x => x.media} imgAspect={1} uploadType={MediaType.IMAGE} label="select category image" description='edit media - select category image description' minimized />
                                <EditMedia<CategoryDownloadDto> getter={x => x.banner!} imgAspect={16 / 9} uploadType={MediaType.IMAGE} label="select banner image" description='edit media - select banner image description' minimized />
                            </div>
                        </EditPartContainer>

                        <div ref={scrollHelper.getRef("svg images")} />
                        <EditPartContainer icon="image effects" title='' translatedTitle={t("svg images")} {...uEPC}>
                            <div style={{ display: "grid", gridTemplateColumns: isSmallDevice ? "1fr" : "1fr 1fr", columnGap: isSmallDevice ? 0 : 80 }}>
                                <EditMedia<CategoryDownloadDto> getter={x => x.svgImage} uploadType={MediaType.SVG} label="select svg pane image" description='edit media - select svg pane image description' minimized />
                                <EditMedia<CategoryDownloadDto> getter={x => x.svgImageAlt} uploadType={MediaType.SVG} label="select svg button image" description='edit media - select svg button description' minimized />
                            </div>
                        </EditPartContainer>

                        {renderMoreContent}

                        {/* <CMSContentInfo /> */}
                        <Divider color="@lightGrey" />
                        <T.ContainerBottom>
                            <Button
                                floated='left'
                                kind="cancelButton"
                                icon="times"
                                content={t("cancel")}
                                onClick={onCancel}
                            />
                            <div ref={scrollHelper.getRef("bottom")} />
                            <Button
                                floated='right'
                                kind="primary"
                                icon="save"
                                disabled={!needSave}
                                content={t("save")}
                                onClick={saveInt}
                                tooltip={{
                                    tooltipText: missing.length > 0 ? t("missing elements") : _.isEqual(originalPost, post) ? t("no changes have been detected") : t("save"),
                                    position: "bottom-end"
                                }}
                            />
                        </T.ContainerBottom>
                    </T.Container>
                </EditPartContainer>
            </div >
        </>
    );
}

export default CategoryEdit;